import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/stores';
import { Loader2 } from 'lucide-react';
import api from '@/lib/api';
import type { ApiResponse, User } from '@/types';

export function OAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const accessToken = searchParams.get('access_token');
    const refreshToken = searchParams.get('refresh_token');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      const errorMessages: Record<string, string> = {
        oauth_failed: 'OAuth 登录失败',
        invalid_state: '无效的状态参数',
        oauth_not_configured: 'OAuth 未配置',
        token_exchange_failed: '令牌交换失败',
        no_email: '无法获取邮箱地址',
      };
      setError(errorMessages[errorParam] || '登录失败');
      return;
    }

    if (!accessToken || !refreshToken) {
      setError('缺少认证令牌');
      return;
    }

    // Store tokens and fetch user profile
    const completeAuth = async () => {
      try {
        // Temporarily store tokens for API call
        localStorage.setItem('access_token', accessToken);
        localStorage.setItem('refresh_token', refreshToken);

        // Fetch user profile
        const response = await api.get<ApiResponse<{ user: User }>>('/auth/me');
        if (!response.data.success || !response.data.data) {
          throw new Error('Failed to get user profile');
        }

        const user = response.data.data.user;

        // Set full auth state with user info
        setAuth(user, {
          access_token: accessToken,
          refresh_token: refreshToken,
          expires_in: 3600,
        });

        // Redirect to home
        navigate('/', { replace: true });
      } catch (err) {
        console.error('OAuth callback error:', err);
        // Clean up on error
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        setError('登录失败，请重试');
      }
    };

    completeAuth();
  }, [searchParams, setAuth, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-bold text-destructive">登录失败</h1>
          <p className="text-muted-foreground">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            返回登录
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
        <p className="text-muted-foreground">正在登录...</p>
      </div>
    </div>
  );
}
